function addToCart(productId, recentlyViewedProduct, variantId) {
  const form = document.querySelector(`#form_${productId}`);
  const id = variantId
    ? variantId
    : form.querySelector(`#product_id_${productId}`).value;
  const quantityInput = recentlyViewedProduct
    ? document.querySelector(`#quantity_recently-viewed_${productId}`)
    : form.querySelector(`#quantity_${productId}`);

  const loader = document.querySelector(".loader");

  loader.style.display = "inline-flex"; // show the loading indicator while waiting for the response

  fetch("/cart/add.js", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      id: id,
      quantity: quantityInput.value,
    }),
  })
    .then((response) => {
      loader.style.display = "none"; // hide the loading indicator after receiving the response
      if (response.ok) {
        // success
        getCartItems();
      } else {
        // handle error response
        console.error(response.statusText);
      }
    })
    .catch((error) => {
      console.error(error);
      loader.style.display = "none";
    });
}

const shopConfig = {
  HR: { currency: "EUR", locale: "hr" },
  RS: { currency: "RSD", locale: "sr" },
  BA: { currency: "BAM", locale: "bs" },
};

const defaultShopConfig = { currency: "EUR", locale: "hr" };

function formatPrice(price) {
  const formattedPrice = price / 100;
  const { currency, locale } = shopConfig[shopLocation] || defaultShopConfig;

  const priceWithCurrency = Intl.NumberFormat(locale, {
    style: "currency",
    currency: currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  return priceWithCurrency.format(formattedPrice);
}

/* -------------------------------------------------------------------------
 * Cart milestones ("leveli")
 *
 * Config + markup: snippets/cart-milestones.liquid (rendered in the drawer and
 * on the cart page). Levels come from global_campaign.milestones metaobjects.
 *
 * Free products are priced at 0 by the milestone gift discount function
 * (automatic discount, product metafield `milestone.threshold`). The theme
 * only adds/removes the gift line (property `_milestone_gift`) so the customer
 * never has to find it — it never sets prices itself.
 *
 * The discount cannot touch cart contents, so a gift line whose level is gone
 * (metaobject set to draft, `upsell_visible` off, campaign swapped) would stay
 * in the cart forever: the customer has no remove control on it. The theme is
 * therefore responsible for removing such "orphan" gift lines too, and does it
 * even when the milestones block is not rendered at all.
 * ---------------------------------------------------------------------- */
const MILESTONE_GIFT_PROPERTY = "_milestone_gift";
const MILESTONE_FAILED_STORAGE_KEY = "milestoneGiftFailed";

const MILESTONE_REACHED_STORAGE_KEY = "milestoneReached";
const MILESTONE_CELEBRATION_MS = 1400;

const cartMilestonesState = {
  config: null,
  syncing: false,
  // line keys of orphan gift lines already sent for removal this page load,
  // so a failing /cart/change.js cannot loop through getCartItems forever
  orphanRemovalAttempted: new Set(),
  // handles of the levels reached at the last render (null = nothing rendered yet)
  lastReached: null,
  // false until the first render of this page load, so the fill can start its
  // animation from the value saved before the last change (see renderCartMilestones)
  fillPrimed: false,
};

const MILESTONE_FILL_STORAGE_KEY = "milestoneFillPercent";

function readStoredFillPercent() {
  try {
    const value = sessionStorage.getItem(MILESTONE_FILL_STORAGE_KEY);
    return value === null ? null : parseFloat(value);
  } catch (error) {
    return null;
  }
}

function storeFillPercent(percent) {
  try {
    sessionStorage.setItem(MILESTONE_FILL_STORAGE_KEY, String(percent));
  } catch (error) {
    // sessionStorage unavailable: the fill simply animates from its current width
  }
}

function isCartPage() {
  return /\/cart\/?$/.test(window.location.pathname);
}

function getMilestonesConfig() {
  if (cartMilestonesState.config) return cartMilestonesState.config;

  const script = document.querySelector("[data-milestones-config]");
  if (!script) return null;

  try {
    const config = JSON.parse(script.textContent);
    config.levels = (config.levels || [])
      .filter((level) => typeof level.threshold === "number")
      .sort((a, b) => a.threshold - b.threshold);
    cartMilestonesState.config = config;
    return config;
  } catch (error) {
    console.error("Cart milestones: invalid config", error);
    return null;
  }
}

function getFailedMilestoneGifts() {
  try {
    return new Set(
      JSON.parse(sessionStorage.getItem(MILESTONE_FAILED_STORAGE_KEY) || "[]"),
    );
  } catch (error) {
    return new Set();
  }
}

function markMilestoneGiftFailed(handle) {
  const failed = getFailedMilestoneGifts();
  failed.add(handle);
  try {
    sessionStorage.setItem(
      MILESTONE_FAILED_STORAGE_KEY,
      JSON.stringify(Array.from(failed)),
    );
  } catch (error) {
    // sessionStorage unavailable: the in-memory guard in syncMilestoneGifts still applies
  }
}

function findMilestoneGiftLine(cart, level) {
  return (cart.items || []).find(
    (item) =>
      item.properties &&
      item.properties[MILESTONE_GIFT_PROPERTY] === level.handle,
  );
}

/**
 * Gift lines whose level is no longer configured: the level metaobject was set
 * to draft / deactivated, or the whole block is hidden (no config at all).
 */
function findOrphanMilestoneGiftLines(cart, config) {
  const activeHandles = new Set(
    config ? config.levels.map((level) => level.handle) : [],
  );
  return (cart.items || []).filter((item) => {
    const handle = item.properties && item.properties[MILESTONE_GIFT_PROPERTY];
    return handle && !activeHandles.has(handle);
  });
}

function computeMilestoneState(cart, config) {
  const levels = config.levels;
  // Orphan gift lines are removed by syncMilestoneGifts; until then they must
  // not count toward the thresholds either (they may no longer be free).
  const giftLines = levels
    .map((level) => findMilestoneGiftLine(cart, level))
    .filter(Boolean)
    .concat(findOrphanMilestoneGiftLines(cart, config));
  // A gift line that is NOT discounted to 0 (discount missing) must not count toward the thresholds
  const giftTotal = giftLines.reduce(
    (sum, line) => sum + (line.final_line_price || 0),
    0,
  );
  const base = Math.max(0, (cart.total_price || 0) - giftTotal);

  const reached = levels.filter((level) => base >= level.threshold);
  const next = levels.find((level) => base < level.threshold) || null;
  const earnedValue = reached.reduce(
    (sum, level) => sum + (level.rewardValue || 0),
    0,
  );

  // Nodes are evenly spaced but stop short of the right edge (NODE_RIGHT_MARGIN)
  // so the last node and its overflowing product image sit inside the track
  // instead of clipping. The fill uses the same scale so it stays aligned with
  // the nodes; when every level is reached fillPercent is 100, so the filled bar
  // still runs past the last node to the edge (as in the design).
  const NODE_RIGHT_MARGIN = 6; // percent of the track reserved on the right
  const nodePosition = (index) =>
    ((index + 1) / levels.length) * (100 - NODE_RIGHT_MARGIN);
  let fillPercent = 100;
  if (next) {
    const nextIndex = levels.indexOf(next);
    const segmentStart = nextIndex === 0 ? 0 : nodePosition(nextIndex - 1);
    const thresholdStart = nextIndex === 0 ? 0 : levels[nextIndex - 1].threshold;
    const segmentProgress =
      (base - thresholdStart) / Math.max(1, next.threshold - thresholdStart);
    fillPercent =
      segmentStart + (nodePosition(nextIndex) - segmentStart) * segmentProgress;
  }

  return {
    base,
    reached,
    next,
    remaining: next ? next.threshold - base : 0,
    earnedValue,
    fillPercent: Math.max(0, Math.min(100, fillPercent)),
    nodePosition,
  };
}

function readStoredReachedMilestones() {
  try {
    const stored = sessionStorage.getItem(MILESTONE_REACHED_STORAGE_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch (error) {
    return null;
  }
}

function storeReachedMilestones(handles) {
  cartMilestonesState.lastReached = handles;
  try {
    sessionStorage.setItem(
      MILESTONE_REACHED_STORAGE_KEY,
      JSON.stringify(handles),
    );
  } catch (error) {
    // sessionStorage unavailable: in-memory state still prevents repeats on this page
  }
}

/**
 * Levels reached now but not at the previous render. On the very first render
 * of a browser session nothing is celebrated (the customer did not just earn it).
 */
function getNewlyReachedMilestones(state) {
  const reachedNow = state.reached.map((level) => level.handle);
  const previous =
    cartMilestonesState.lastReached || readStoredReachedMilestones();

  storeReachedMilestones(reachedNow);

  if (!previous) return [];
  return reachedNow.filter((handle) => !previous.includes(handle));
}

function celebrateMilestoneNode(node) {
  if (!node || node.querySelector(".milestones__burst")) return;

  const burst = document.createElement("span");
  burst.className = "milestones__burst";
  burst.setAttribute("aria-hidden", "true");

  const colors = ["#11848e", "#f79009", "#f04438", "#12b76a", "#6172f3"];
  for (let i = 0; i < 14; i += 1) {
    const particle = document.createElement("i");
    const angle = Math.round((360 / 14) * i + (Math.random() * 16 - 8));
    const distance = Math.round(36 + Math.random() * 26);
    particle.style.setProperty("--angle", `${angle}deg`);
    particle.style.setProperty("--dist", `${distance}px`);
    particle.style.setProperty("--color", colors[i % colors.length]);
    particle.style.setProperty("--delay", `${Math.round(Math.random() * 90)}ms`);
    burst.appendChild(particle);
  }

  node.classList.add("is-celebrating");
  node.appendChild(burst);

  setTimeout(() => {
    node.classList.remove("is-celebrating");
    burst.remove();
  }, MILESTONE_CELEBRATION_MS);
}

function flashMilestoneElement(el) {
  if (!el) return;
  el.classList.remove("is-flash");
  // restart the animation if it is already running
  void el.offsetWidth;
  el.classList.add("is-flash");
  setTimeout(() => el.classList.remove("is-flash"), MILESTONE_CELEBRATION_MS);
}

function celebrateMilestones(newlyReached, state) {
  if (!newlyReached.length) return;


  document.querySelectorAll("[data-cart-milestones]").forEach((root) => {
    newlyReached.forEach((handle) => {
      celebrateMilestoneNode(
        root.querySelector(`[data-milestone-node="${handle}"]`),
      );
    });
    flashMilestoneElement(
      root.querySelector("[data-milestones-earned-row-inline]"),
    );
    if (!state.next) {
      flashMilestoneElement(root.querySelector("[data-milestones-done-row]"));
    }
  });
}

function renderCartMilestones(state, config) {
  const levels = config.levels;
  const newlyReached = getNewlyReachedMilestones(state);

  document.querySelectorAll("[data-cart-milestones]").forEach((root) => {
    const activeText = state.next ? state.next.handle : "done";
    root.querySelectorAll("[data-milestones-text]").forEach((el) => {
      el.hidden = el.dataset.milestonesText !== activeText;
    });

    const nextRow = root.querySelector("[data-milestones-next-row]");
    const doneRow = root.querySelector("[data-milestones-done-row]");
    if (nextRow) nextRow.hidden = !state.next;
    if (doneRow) doneRow.hidden = !!state.next;

    const remaining = root.querySelector("[data-milestones-remaining]");
    if (remaining) remaining.textContent = formatPrice(state.remaining);

    const fill = root.querySelector("[data-milestones-fill]");
    if (fill) {
      // On the first render after a page load, jump the fill (without animating)
      // to the value saved before the last change, so it then animates from
      // there to the new value: growing right on an increase, shrinking left on
      // a decrease, instead of always re-filling from zero.
      if (!cartMilestonesState.fillPrimed) {
        const previous = readStoredFillPercent();
        if (previous !== null) {
          fill.style.transition = "none";
          fill.style.width = previous + "%";
          void fill.offsetWidth; // force reflow so the jump applies first
          fill.style.transition = "";
        }
      }
      fill.style.width = state.fillPercent + "%";
    }

    const nodesContainer = root.querySelector("[data-milestones-nodes]");
    levels.forEach((level, index) => {
      const node = root.querySelector(
        `[data-milestone-node="${level.handle}"]`,
      );
      if (node) {
        node.style.left = state.nodePosition(index) + "%";
        node.classList.toggle("is-reached", state.reached.includes(level));
        node.classList.toggle("is-last", index === levels.length - 1);
        // Keep DOM order = threshold order (admin list order may differ)
        if (nodesContainer) nodesContainer.appendChild(node);
      }

      const thumb = root.querySelector(
        `[data-milestone-thumb="${level.handle}"]`,
      );
      if (thumb) {
        thumb.hidden = !state.reached.includes(level);
        if (thumb.parentElement) thumb.parentElement.appendChild(thumb);
      }
    });
  });

  document.querySelectorAll("[data-milestones-earned-value]").forEach((el) => {
    el.textContent = formatPrice(state.earnedValue);
  });
  // Summary rows (drawer footer, cart page aside) are rendered hidden and only
  // shown when milestones are active
  document.querySelectorAll("[data-milestones-earned-row]").forEach((el) => {
    el.hidden = false;
  });

  // Remember this fill so the next page load can animate from it, and mark the
  // fill primed so later renders in this page load animate from their current width.
  storeFillPercent(state.fillPercent);
  cartMilestonesState.fillPrimed = true;

  // Celebrate after the DOM reflects the new state so the fill animates first,
  // and after the drawer slide-in (300 ms) so the customer actually sees it
  if (newlyReached.length) {
    setTimeout(() => celebrateMilestones(newlyReached, state), 400);
  }
}

function runMilestoneCartAction(action) {
  const url = action.op === "add" ? "/cart/add.js" : "/cart/change.js";
  const body =
    action.op === "add"
      ? {
          items: [
            {
              id: action.id,
              quantity: 1,
              properties: { [MILESTONE_GIFT_PROPERTY]: action.handle },
            },
          ],
        }
      : { id: action.id, quantity: action.quantity };

  return fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  }).then((response) => {
    if (!response.ok) {
      throw new Error(`Cart milestones: ${url} responded ${response.status}`);
    }
    return response.json();
  });
}

function syncMilestoneGifts(cart, state, config, options) {
  if (cartMilestonesState.syncing) return Promise.resolve(false);

  const failed = getFailedMilestoneGifts();
  const actions = [];

  // Orphans first: their level is gone (draft metaobject, block hidden), the
  // customer cannot remove them and the discount may still price them at 0 —
  // or, worse, no longer do so. Only on page load (options.cleanupOrphans, see
  // initCartMilestones): a runtime sync reuses the config cached at page load,
  // and a tab left open across an admin change must never remove a gift that
  // another, freshly loaded tab added legitimately.
  const orphans = options.cleanupOrphans
    ? findOrphanMilestoneGiftLines(cart, config)
    : [];
  orphans.forEach((line) => {
    if (cartMilestonesState.orphanRemovalAttempted.has(line.key)) return;
    cartMilestonesState.orphanRemovalAttempted.add(line.key);
    console.warn(
      `Cart milestones: gift "${line.properties[MILESTONE_GIFT_PROPERTY]}" belongs to an inactive level, removing it.`,
    );
    actions.push({ op: "change", id: line.key, quantity: 0 });
  });

  const levels = config ? config.levels : [];
  levels.forEach((level) => {
    if (level.type !== "free_product" || !level.variantId) return;

    const giftLine = findMilestoneGiftLine(cart, level);
    const reached = state.base >= level.threshold;
    const canAdd = reached && level.available && !failed.has(level.handle);

    if (giftLine && giftLine.variant_id !== level.variantId) {
      // The level's gift product was swapped in the admin while this line sat
      // in the cart (same level handle, old product). The old product has no
      // discount any more, so the line must not count as "the gift failed":
      // replace it with the current gift instead of blocking the level.
      actions.push({ op: "change", id: giftLine.key, quantity: 0 });
      if (canAdd) {
        actions.push({ op: "add", id: level.variantId, handle: level.handle });
      }
    } else if (giftLine) {
      if (!reached) {
        actions.push({ op: "change", id: giftLine.key, quantity: 0 });
      } else if (giftLine.quantity > 1) {
        // Two syncs added the gift at the same time (e.g. the customer opened
        // another page while the first add was in flight) and Shopify merged
        // them into one line. The discount function only prices a gift line
        // with quantity 1, so this line is never 0 — reduce it before the
        // price check below would take it for a missing discount.
        actions.push({ op: "change", id: giftLine.key, quantity: 1 });
      } else if (giftLine.final_line_price > 0) {
        // Shopify did not discount the gift: the BXGY discount is missing or
        // does not combine. Remove it so the customer is never charged for it.
        console.warn(
          `Cart milestones: gift "${level.handle}" was not discounted to 0, removing it. Check the milestone gift automatic discount.`,
        );
        markMilestoneGiftFailed(level.handle);
        actions.push({ op: "change", id: giftLine.key, quantity: 0 });
      }
    } else if (canAdd) {
      actions.push({ op: "add", id: level.variantId, handle: level.handle });
    }
  });

  if (!actions.length) return Promise.resolve(false);

  cartMilestonesState.syncing = true;
  return actions
    .reduce(
      (chain, action) => chain.then(() => runMilestoneCartAction(action)),
      Promise.resolve(),
    )
    .catch((error) => console.error(error))
    .then(() => {
      cartMilestonesState.syncing = false;
      // deferReload: the caller (a cart-page quantity change) will do a single
      // reload itself after this sync, so crossing a level threshold does not
      // trigger a second reload here.
      if (options.deferReload) return true;
      if (options.reloadOnMutation) {
        window.location.reload();
      } else if (typeof getCartItems === "function") {
        getCartItems();
      }
      return true;
    });
}

/**
 * @param {object} cart - /cart.js shaped cart
 * @param {object} [options]
 * @param {boolean} [options.sync=true] - add/remove gift lines to match the reached levels
 * @param {boolean} [options.reloadOnMutation=false] - reload the page after a gift add/remove (cart page)
 * @param {boolean} [options.cleanupOrphans=false] - also remove gift lines of inactive levels (page load only)
 */
function handleCartMilestones(cart, options = {}) {
  if (!cart) return Promise.resolve(false);
  // config === null: the milestones block is not rendered (upsell_visible off,
  // no active level). The bar has nothing to show; the only work left is the
  // page-load orphan cleanup, which runs with an empty level list.
  let config = getMilestonesConfig();
  if (config && !config.levels.length) config = null;
  if (!config && !options.cleanupOrphans) return Promise.resolve(false);

  const state = config ? computeMilestoneState(cart, config) : null;
  // render:false lets the cart-page quantity flow sync gifts without animating
  // the bar before its reload — the animations then play once, after the reload.
  if (config && options.render !== false) renderCartMilestones(state, config);

  if (options.sync !== false) {
    return (
      syncMilestoneGifts(cart, state, config, options) || Promise.resolve(false)
    );
  }
  return Promise.resolve(false);
}

function initCartMilestones() {
  // The liquid emits the initial cart JSON when the block is shown OR the cart
  // already holds a gift line (so orphans get cleaned up without a /cart.js
  // request on every page view while milestones are off).
  const initialCart = document.querySelector("[data-milestones-cart]");
  const configElement = document.querySelector("[data-milestones-config]");
  const config = getMilestonesConfig();
  if (!config && !initialCart) return;

  // Orphan cleanup happens only here: the config and the cart snapshot come
  // from the same server render, so "handle not in config" really means the
  // level is inactive. A config element that is present but failed to parse
  // (or lists no levels) is unknown state, not "no levels" — no cleanup then,
  // otherwise a broken config would strip every gift from an active promo.
  const cleanupOrphans = configElement
    ? !!(config && config.levels.length)
    : true;

  let cartPromise;
  if (initialCart) {
    try {
      cartPromise = Promise.resolve(JSON.parse(initialCart.textContent));
    } catch (error) {
      console.error("Cart milestones: invalid initial cart", error);
    }
  }
  if (!cartPromise) {
    cartPromise = fetch("/cart.js").then((response) => response.json());
  }

  cartPromise
    .then((cart) =>
      handleCartMilestones(cart, {
        sync: true,
        reloadOnMutation: isCartPage(),
        cleanupOrphans,
      }),
    )
    .catch((error) => console.error(error));
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initCartMilestones, {
    once: true,
  });
} else {
  initCartMilestones();
}

function updatePriceAndCount(response) {
  const cartTotalPrice = response.total_price;
  const cartItemCount = response.item_count;
  const finalPriceElement = document.querySelectorAll(".final_price_key");
  const oldPriceElement = document.querySelectorAll(".old_price_elem");

  // On the cart page the quantity flow (updateQuantity) syncs gift lines and
  // reloads itself, so here we only refresh the bar without a second sync.
  handleCartMilestones(response, {
    sync: !isCartPage(),
    reloadOnMutation: false,
  });

  // Notification stack (gift progress, cart reminder) follows the cart
  document.dispatchEvent(new CustomEvent("ladria:cart", { detail: response }));

  const cartTotalPriceElements = document.querySelectorAll(".total_price_key");
  for (let i = 0; i < cartTotalPriceElements.length; i++) {
    cartTotalPriceElements[i].textContent = formatPrice(cartTotalPrice);
  }

  for (let i = 0; i < finalPriceElement.length; i++) {
    finalPriceElement[i].textContent = formatPrice(cartTotalPrice);
  }

  for (let i = 0; i < oldPriceElement.length; i++) {
    oldPriceElement[i].textContent = convertEuroToKn(cartTotalPrice);
  }

  const cartItemCountElements = document.querySelectorAll(".item_count_key");
  for (let i = 0; i < cartItemCountElements.length; i++) {
    cartItemCountElements[i].textContent = `(${cartItemCount})`;
  }
}

function updateQuantity(domKey, func, lineKey) {
  // Ignore clicks while a change is already in flight (the counter no longer
  // relies on handleWaitCart for this debounce).
  if (window.__cartQtyBusy) return;
  const form = document.querySelector(`#form_${domKey}`);
  // Target the line by its unique line-item key, not the variant id: once a
  // discount splits a variant across several cart lines, the variant id is
  // ambiguous and /cart/change.js would change the wrong line.
  const id = lineKey || form.querySelector(`#product_id_${domKey}`).value;
  const quantityInput = form.querySelector(`#quantity_${domKey}`);
  const loader = document.querySelector(".loader");

  window.__cartQtyBusy = true;
  loader.style.display = "inline-flex";

  const options = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      id: id,
      quantity:
        func === "increase"
          ? Number(quantityInput.value) + 1
          : Math.max(Number(quantityInput.value) - 1, 0), // 0 removes the line
    }),
  };

  fetch("/cart/change.js", options)
    .then((response) => {
      if (!response.ok) {
        throw new Error("Network response was not ok");
      }
      return response.json();
    })
    .then((data) => {
      loader.style.display = "none";
      // Sync every rendered quantity input from the server response instead of
      // guessing: a BXGY discount can re-split the variant across lines, so the
      // unit may land on a different line than the one clicked. Match by the
      // css-safe line key.
      data.items.forEach((item) => {
        const input = document.querySelector(
          `#quantity_${item.key.replace(/:/g, "_")}`
        );
        if (input) input.value = item.quantity;
      });

      if (isCartPage()) {
        // Cart page rows are server-rendered, so a quantity change reloads once
        // to refresh them. Sync gift lines FIRST, then do that single reload —
        // otherwise crossing a level threshold reloads twice (once for the
        // quantity change, once when the post-reload gift sync mutates the cart).
        handleCartMilestones(data, {
          sync: true,
          reloadOnMutation: false,
          deferReload: true,
          render: false, // let the animations play once, after the reload
        }).then(() => window.location.reload());
      } else {
        window.__cartQtyBusy = false;
        updatePriceAndCount(data);
      }
    })
    .catch((error) => {
      console.error("There was a problem with the fetch operation:", error);
      window.__cartQtyBusy = false;
      loader.style.display = "none";
    });
}

function increase(domKey, lineKey) {
  updateQuantity(domKey, "increase", lineKey);
}

function decrease(domKey, lineKey) {
  updateQuantity(domKey, "decrease", lineKey);
}

function removeItem(id) {
  // `id` is the unique line-item key (may contain a colon), so encode it for
  // the urlencoded body. Removing by key targets exactly one split line.
  const productId = encodeURIComponent(id);

  // select the loading indicator element
  const loader = document.querySelector(".loader");
  loader.style.display = "inline-flex";

  const options = {
    method: "POST",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
    },
    body: `quantity=0&id=${productId}`,
  };

  fetch("/cart/change.js", options)
    .then((response) => {
      if (!response.ok) {
        throw new Error("Network response was not ok");
      }
      return response.json();
    })
    .then((data) => {
      // Cart updated successfully
      getCartItems();
      updatePriceAndCount(data);
    })
    .catch((error) => {
      console.error("There was a problem with the fetch operation:", error);
      loader.style.display = "none";
    });
}

function handleAddToCartPopup() {
  const popupElement = document.querySelector(".cart__popup");
  popupElement.classList.add("active");

  setTimeout(() => {
    popupElement.classList.remove("active");
  }, 10000);
}

function hideAddToCartPopup() {
  const popupElement = document.querySelector(".cart__popup");
  popupElement.classList.remove("active");
}
